import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { FiUsers, FiUserCheck, FiEye, FiRepeat, FiUserPlus } from 'react-icons/fi';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import { API_URL } from '../../Api/api';
import { getAuthToken } from '../../Api/authToken';

const PERIODS = [
  { id: 'daily', label: 'Daily' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'yearly', label: 'Yearly' },
];

const PIE_COLORS = ['#c8a04a', '#3b82f6', '#10b981', '#8b5cf6'];

const VisitorAnalyticsSection = () => {
  const { pathname } = useLocation();
  const [period, setPeriod] = useState('daily');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchVisitors = useCallback(async () => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API_URL}/admin/analytics/visitors`, {
        params: { period },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      console.error('Visitor analytics fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    setLoading(true);
    fetchVisitors();
    const t = setInterval(fetchVisitors, 15000);
    return () => clearInterval(t);
  }, [fetchVisitors]);

  if (!pathname.startsWith('/admin')) return null;

  const summary = data?.summary || {};

  const statCards = [
    { title: 'Logged-in Visitors', value: summary.totalLoggedInVisitors ?? 0, icon: <FiUserCheck />, color: '#3b82f6' },
    { title: 'Guest Visitors', value: summary.totalGuestVisitors ?? 0, icon: <FiUsers />, color: '#c8a04a' },
    { title: 'Total Visits', value: summary.totalVisits ?? 0, icon: <FiEye />, color: '#10b981' },
    { title: 'New Visitors', value: summary.newVisitors ?? 0, icon: <FiUserPlus />, color: '#8b5cf6' },
    { title: 'Returning Visitors', value: summary.returningVisitors ?? 0, icon: <FiRepeat />, color: '#14b8a6' },
  ];

  return (
    <section className="visitor-analytics-section">
      <div className="visitor-analytics-header">
        <div>
          <h2 className="visitor-analytics-title">Visitor Analytics</h2>
          <p className="visitor-analytics-sub">Logged-in users and guest traffic on the website</p>
        </div>
        <div className="visitor-analytics-filters" role="tablist" aria-label="Visitor period">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              type="button"
              role="tab"
              aria-selected={period === p.id}
              className={`visitor-analytics-filter${period === p.id ? ' is-active' : ''}`}
              onClick={() => setPeriod(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading && !data ? (
        <div className="visitor-analytics-skeleton admin-card" />
      ) : (
        <>
          <div className="visitor-analytics-stats">
            {statCards.map((stat) => (
              <div key={stat.title} className="admin-card visitor-analytics-stat">
                <div className="visitor-analytics-stat__icon" style={{ backgroundColor: `${stat.color}15`, color: stat.color }}>
                  {stat.icon}
                </div>
                <div>
                  <p className="visitor-analytics-stat__value">{Number(stat.value).toLocaleString('en-IN')}</p>
                  <p className="visitor-analytics-stat__label">{stat.title}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="analytics-charts-grid visitor-analytics-charts">
            <div className="admin-card chart-card chart-wide">
              <h2>Visitor Trend</h2>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data?.chart || []}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                    <YAxis allowDecimals={false} />
                    <Tooltip cursor={{ fill: '#f8fafc' }} />
                    <Legend />
                    <Bar dataKey="loggedIn" name="Logged-in" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="guest" name="Guest" fill="#c8a04a" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="admin-card chart-card">
              <h2>Logged-in vs Guest</h2>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data?.visitorBreakdown || []}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={90}
                      paddingAngle={4}
                    >
                      {(data?.visitorBreakdown || []).map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="admin-card chart-card">
              <h2>New vs Returning</h2>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data?.newVsReturning || []}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={90}
                      paddingAngle={4}
                    >
                      {(data?.newVsReturning || []).map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[(i + 2) % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default VisitorAnalyticsSection;
